import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

// Import logo dari local assets
import logoImage from "../../assets/images/logo/solusiiotcerdas.png";

export default function Navbar() {
  const location = useLocation();
  const { siteSettings, theme, toggleTheme } = useApp();
  const { isAuthenticated, logout } = useAuth();
  const { totalItems } = useCart();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // State untuk Dropdown Desktop
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  
  // State untuk Dropdown Mobile
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);

  const closeTimeoutRef = useRef(null);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/marketplace", label: "Marketplace" },
    { to: "/blog", label: "Blog" },
    { to: "/gallery", label: "Gallery" },
    { to: "/contact", label: "Contact" },
  ];

  // Pemisahan Logis: Hardware vs Solusi
  const categories = [
    { to: "/categories/mikrokontroler", label: "Mikrokontroler" },
    { to: "/categories/modul-komponen", label: "Modul & Komponen" },
    { to: "/categories/paket-starter-kit", label: "Starter Kit" },
    { to: "/categories/sensor-air-flowmeter", label: "Sensor Air Flowmeter" },
    { to: "/categories/sensor-gas-udara", label: "Sensor Gas & Udara" },
    { to: "/categories/sensor-suhu-kelembaban", label: "Sensor Suhu & Kelembaban" },
    { to: "/categories/sensor-tanah-pertanian", label: "Sensor Tanah & Pertanian" },
  ];

  const solutions = [
    { to: "/solutions/depot-air-cerdas", label: "Depot Air Cerdas" },
    { to: "/solutions/smart-parking", label: "Smart Parking" },
    { to: "/solutions/smart-farming", label: "Smart Farming" },
    { to: "/solutions/smart-home", label: "Smart Home" },
    { to: "/solutions/watermonitor", label: "Smart Water Monitor" },
    { to: "/solutions/consultation", label: "Konsultasi Sistem" },
  ];

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  const isCategoryActive = () => location.pathname.startsWith("/categories");
  const isSolutionsActive = () => location.pathname.startsWith("/solutions");

  const handleLogout = () => {
    logout();
    closeAllMenus();
  };

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setCategoryOpen(false);
    setSolutionsOpen(false);
    setMobileCategoryOpen(false);
    setMobileSolutionsOpen(false);

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // Handlers untuk Desktop Hover (Kategori)
  const handleCategoryEnter = () => {
    clearTimeout(closeTimeoutRef.current);
    setSolutionsOpen(false); // Tutup menu lain
    setCategoryOpen(true);
  };
  const handleCategoryLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setCategoryOpen(false), 180);
  };

  // Handlers untuk Desktop Hover (Solusi)
  const handleSolutionsEnter = () => {
    clearTimeout(closeTimeoutRef.current);
    setCategoryOpen(false); // Tutup menu lain
    setSolutionsOpen(true);
  };
  const handleSolutionsLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setSolutionsOpen(false), 180);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    closeAllMenus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Kunci scroll body saat menu mobile terbuka (Optimasi Mobile)
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  return (
    <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/95">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 lg:px-6">
        
        {/* Logo Section */}
        <Link to="/" className="flex items-center gap-3 active:opacity-80 transition-opacity" onClick={closeAllMenus}>
          <img 
            src={logoImage} 
            alt="Solusi IoT Cerdas" 
            // Mengganti rounded-full & object-cover menjadi object-contain agar logo asli tidak terpotong
            className="h-9 w-auto sm:h-10 object-contain drop-shadow-sm" 
          />
          <div className="hidden sm:block">
            <h1 className="text-sm font-bold leading-tight text-gray-900 dark:text-white">
              {siteSettings?.site_name || "Solusi IoT Cerdas"}
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Marketplace & IoT Solutions</p>
          </div>
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden items-center gap-5 xl:gap-6 lg:flex">
          {navLinks.slice(0, 3).map((link) => (
             <Link
               key={link.to}
               to={link.to}
               className={`text-sm font-medium transition ${isActive(link.to) ? "text-blue-600 dark:text-blue-400" : "text-gray-700 hover:text-blue-600 dark:text-gray-200"}`}
             >
               {link.label}
             </Link>
          ))}

          {/* Categories Dropdown Desktop */}
          <div className="relative" onMouseEnter={handleCategoryEnter} onMouseLeave={handleCategoryLeave}>
            <button type="button" className={`flex items-center gap-1 py-2 text-sm font-medium transition ${isCategoryActive() ? "text-blue-600 dark:text-blue-400" : "text-gray-700 hover:text-blue-600 dark:text-gray-200"}`}>
              Categories
              <svg className={`h-4 w-4 transition-transform ${categoryOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            </button>
            <div className="absolute left-0 top-full h-3 w-64" />
            <div className="absolute left-0 top-full pt-2">
              {categoryOpen && (
                <div className="w-64 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
                  <div className="p-2">
                    {categories.map((cat) => (
                      <Link key={cat.to} to={cat.to} className={`block rounded-xl px-4 py-3 text-sm transition ${isActive(cat.to) ? "bg-blue-50 text-blue-600 dark:bg-gray-700" : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"}`}>
                        {cat.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Solutions Dropdown Desktop */}
          <div className="relative" onMouseEnter={handleSolutionsEnter} onMouseLeave={handleSolutionsLeave}>
            <button type="button" className={`flex items-center gap-1 py-2 text-sm font-medium transition ${isSolutionsActive() ? "text-blue-600 dark:text-blue-400" : "text-gray-700 hover:text-blue-600 dark:text-gray-200"}`}>
              Solutions
              <svg className={`h-4 w-4 transition-transform ${solutionsOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            </button>
            <div className="absolute left-0 top-full h-3 w-64" />
            <div className="absolute left-0 top-full pt-2">
              {solutionsOpen && (
                <div className="w-64 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
                  <div className="p-2">
                    {solutions.map((sol) => (
                      <Link key={sol.to} to={sol.to} className={`block rounded-xl px-4 py-3 text-sm transition ${isActive(sol.to) ? "bg-blue-50 text-blue-600 dark:bg-gray-700" : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"}`}>
                        {sol.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {navLinks.slice(3).map((link) => (
             <Link key={link.to} to={link.to} className={`text-sm font-medium transition ${isActive(link.to) ? "text-blue-600 dark:text-blue-400" : "text-gray-700 hover:text-blue-600 dark:text-gray-200"}`}>
               {link.label}
             </Link>
          ))}
        </nav>

        {/* Right Side Tools */}
        <div className="hidden items-center gap-3 lg:flex">
          <button onClick={toggleTheme} className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200">{theme === "dark" ? "Light" : "Dark"}</button>
          <Link to="/cart" className="relative rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200">
            Cart
            {totalItems > 0 && <span className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">{totalItems}</span>}
          </Link>
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Dashboard</Link>
              <button onClick={handleLogout} className="rounded-xl border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-800">Logout</button>
            </>
          ) : (
            <Link to="/login" className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Login</Link>
          )}
        </div>

        {/* Mobile Toggle Button */}
        <div className="flex items-center gap-4 lg:hidden">
          {/* Cart Icon for Mobile (Fast Access) */}
          <Link to="/cart" className="relative p-2 text-gray-700 dark:text-gray-200" onClick={closeAllMenus}>
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
            {totalItems > 0 && <span className="absolute 0 top-1 right-0 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{totalItems}</span>}
          </Link>
          
          <button type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="rounded-xl border border-gray-200 p-2 text-gray-700 active:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:active:bg-gray-800">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              {mobileMenuOpen ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="border-t border-gray-200 bg-white px-4 py-4 h-[calc(100vh-64px)] overflow-y-auto overscroll-contain dark:border-gray-800 dark:bg-gray-900 lg:hidden">
          <div className="space-y-3 pb-20">
            {/* Nav Links atas */}
            <div className="space-y-1">
              {navLinks.slice(0, 3).map((link) => (
                <Link key={link.to} to={link.to} onClick={closeAllMenus} className={`block rounded-xl px-4 py-3.5 text-sm font-medium ${isActive(link.to) ? "bg-blue-50 text-blue-600 dark:bg-gray-800/80" : "text-gray-700 dark:text-gray-200 active:bg-gray-50 dark:active:bg-gray-800"}`}>
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Mobile Categories Dropdown */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700">
              <button onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)} className="flex w-full items-center justify-between px-4 py-3.5 text-sm font-medium text-gray-700 dark:text-gray-200 active:bg-gray-50 rounded-xl dark:active:bg-gray-800">
                <span>Categories</span>
                <svg className={`h-4 w-4 transition-transform ${mobileCategoryOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {mobileCategoryOpen && (
                <div className="space-y-1 px-2 pb-2 bg-gray-50/50 dark:bg-gray-800/30 pt-1 rounded-b-xl border-t border-gray-100 dark:border-gray-700/50">
                  {categories.map((cat) => (
                    <Link key={cat.to} to={cat.to} onClick={closeAllMenus} className="block rounded-lg px-4 py-3 text-sm text-gray-600 dark:text-gray-300 active:bg-blue-50 dark:active:bg-gray-700">{cat.label}</Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Solutions Dropdown */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700">
              <button onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)} className="flex w-full items-center justify-between px-4 py-3.5 text-sm font-medium text-gray-700 dark:text-gray-200 active:bg-gray-50 rounded-xl dark:active:bg-gray-800">
                <span>IoT Solutions</span>
                <svg className={`h-4 w-4 transition-transform ${mobileSolutionsOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {mobileSolutionsOpen && (
                <div className="space-y-1 px-2 pb-2 bg-gray-50/50 dark:bg-gray-800/30 pt-1 rounded-b-xl border-t border-gray-100 dark:border-gray-700/50">
                  {solutions.map((sol) => (
                    <Link key={sol.to} to={sol.to} onClick={closeAllMenus} className="block rounded-lg px-4 py-3 text-sm text-gray-600 dark:text-gray-300 active:bg-blue-50 dark:active:bg-gray-700">{sol.label}</Link>
                  ))}
                </div>
              )}
            </div>

            {/* Nav Links Bawah */}
            <div className="space-y-1">
              {navLinks.slice(3).map((link) => (
                <Link key={link.to} to={link.to} onClick={closeAllMenus} className={`block rounded-xl px-4 py-3.5 text-sm font-medium ${isActive(link.to) ? "bg-blue-50 text-blue-600 dark:bg-gray-800/80" : "text-gray-700 dark:text-gray-200 active:bg-gray-50 dark:active:bg-gray-800"}`}>
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Bottom Actions Mobile */}
            <div className="grid grid-cols-1 gap-3 pt-4 mt-2 border-t border-gray-200 dark:border-gray-700">
              <button onClick={toggleTheme} className="w-full rounded-xl border border-gray-200 px-4 py-3.5 text-sm font-medium text-gray-700 active:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:active:bg-gray-800">
                {theme === "dark" ? "Beralih ke Light Mode" : "Beralih ke Dark Mode"}
              </button>
              
              {isAuthenticated ? (
                <div className="grid grid-cols-2 gap-3">
                  <Link to="/dashboard" onClick={closeAllMenus} className="rounded-xl bg-blue-600 px-4 py-3.5 text-center text-sm font-medium text-white active:bg-blue-700">Dashboard</Link>
                  <button onClick={handleLogout} className="rounded-xl border border-red-200 bg-red-50/50 px-4 py-3.5 text-sm font-medium text-red-600 active:bg-red-100 dark:border-red-900/50 dark:bg-red-900/20">Logout</button>
                </div>
              ) : (
                <Link to="/login" onClick={closeAllMenus} className="block w-full rounded-xl bg-blue-600 px-4 py-3.5 text-center text-sm font-medium text-white active:bg-blue-700">Login / Register</Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}